import { createApi, fetchBaseQuery, FetchBaseQueryError } from '@reduxjs/toolkit/query/react'
import { apiPath, baseUrl } from 'config'
import { User } from 'types'
import { AddressString } from 'types/index'
import { privateKeyToAccount } from 'viem/accounts'

import { getSignature } from 'utils/getSignature'

import { connect, ConnectData, connectUnauthorized, disconnect } from './slice'
import { LoginBody, LoginRequest } from './type'

export const queryAuth = createApi({
  baseQuery: fetchBaseQuery({ baseUrl }),
  endpoints: (builder) => ({
    connectUnauthorized: builder.query<ConnectData, AddressString>({
      queryFn: async (investorAddress, api, _extraOptions, baseQuery) => {
        const { data } = await baseQuery({
          method: 'GET',
          url: apiPath.connect(investorAddress),
        })
        if (data) {
          api.dispatch(connectUnauthorized({ address: investorAddress, data: data as ConnectData }))
          return { data: data as ConnectData }
        }
        return { error: data as FetchBaseQueryError }
      },
    }),
    login: builder.mutation<User, LoginRequest>({
      queryFn: async (arg, api, _extraOptions, baseQuery) => {
        const newPrivateAccount = privateKeyToAccount(arg.privateKey)

        const body: LoginBody = {
          acceptedTerms: arg.dateTime,
          sessionAddress: newPrivateAccount.address as AddressString,
          userAddress: arg.userAddress,
        }

        if (arg.utmMark) {
          body.utmMark = arg.utmMark
        }

        const { data } = await baseQuery({
          body,
          headers: { deadline: String(arg.deadline), signature: arg.signature },
          method: 'POST',
          url: apiPath.login,
        })
        if (data) {
          api.dispatch(
            connect({
              account: arg.userAddress,
              deadline: arg.deadline,
              isSignedIn: true,
              signAcountPrivateKey: arg.privateKey,
              signature: arg.signature,
              userData: data as User,
            }),
          )
          return { data: data as User }
        }
        return { error: data as FetchBaseQueryError }
      },
    }),
    logout: builder.mutation<boolean, void>({
      queryFn: async (_arg, api, _extraOptions, baseQuery) => {
        const { deadline, signature } = await getSignature({
          primaryType: 'Logout',
          url: apiPath.logout,
        })

        api.dispatch(disconnect())

        const { data } = await baseQuery({
          headers: { deadline: `${deadline}`, signature },
          method: 'POST',
          url: apiPath.logout,
        })

        if (!data) {
          return { error: data as FetchBaseQueryError }
        }

        return { data: data as boolean }
      },
    }),
  }),
  reducerPath: 'auth',
})

export const { useLazyConnectUnauthorizedQuery, useLoginMutation, useLogoutMutation } = queryAuth
